import { useState } from "react";
import { Cpu, MemoryStick, HardDrive, Zap, RadioTower, Save, Layers } from "lucide-react";
import { PRESETS, ACCELERATORS } from "../data/presets";
import { detectHardware } from "../lib/compute";
import { useHardware } from "../context/HardwareContext";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

function Field({ icon: Icon, label, children }) {
  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-primary" /> {label}
      </Label>
      {children}
    </div>
  );
}

export function HardwarePanel() {
  const { hardware, setHardware, applyHardware } = useHardware();
  const { user } = useAuth();
  const [detecting, setDetecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [profileName, setProfileName] = useState("");
  const gpuCount = hardware.gpuCount || 1;

  const detect = async () => {
    setDetecting(true);
    const d = await detectHardware();
    if (Object.keys(d).length) {
      setHardware(d);
      toast.success("Matériel détecté partiellement — ajustez les valeurs si besoin.");
    } else {
      toast.info("Détection navigateur indisponible. Renseignez manuellement.");
    }
    setDetecting(false);
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    if (!profileName.trim()) return;
    setSaving(true);
    try {
      await api.post("/profiles", { name: profileName.trim(), hardware });
      toast.success("Profil sauvegardé.");
      setSaveOpen(false);
    } catch (err) {
      console.error("Sauvegarde du profil impossible", err);
      toast.error("Échec de la sauvegarde.");
    }
    setSaving(false);
  };

  const openSave = () => {
    if (!user) {
      toast.error("Connectez-vous pour sauvegarder un profil.");
      return;
    }
    setProfileName(hardware.gpu || "Mon PC");
    setSaveOpen(true);
  };

  return (
    <div className="rounded-xl border border-[#1E2638] bg-[#0F141C] p-5 md:p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-head text-lg font-bold">Analyseur matériel</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={detect} disabled={detecting} data-testid="hardware-detect-button" className="gap-1.5">
            <RadioTower className="h-4 w-4" /> {detecting ? "Détection…" : "Détecter"}
          </Button>
          <Button variant="secondary" size="sm" onClick={openSave} disabled={saving} data-testid="hardware-save-button" className="gap-1.5">
            <Save className="h-4 w-4" /> Sauver
          </Button>
          <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
            <DialogContent className="border-[#1E2638] bg-[#0F141C]">
              <DialogHeader><DialogTitle className="font-head">Sauvegarder ce profil matériel</DialogTitle></DialogHeader>
              <form onSubmit={saveProfile} className="space-y-4">
                <Input value={profileName} maxLength={100} onChange={(e) => setProfileName(e.target.value)} autoFocus data-testid="profile-name-input" placeholder="Nom du profil" />
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setSaveOpen(false)} data-testid="profile-save-cancel">Annuler</Button>
                  <Button type="submit" disabled={saving || !profileName.trim()} data-testid="profile-save-confirm">Enregistrer</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="mb-5">
        <Label className="mb-2 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Presets rapides</Label>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              onClick={() => applyHardware(p.hw)}
              data-testid={p.testid}
              className="rounded-md border border-[#1E2638] bg-[#080B10] px-3 py-1.5 font-mono2 text-xs text-slate-300 transition-colors hover:border-primary hover:text-primary"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field icon={Cpu} label={`CPU — ${hardware.cpuCores} cœurs`}>
          <Slider value={[hardware.cpuCores]} min={2} max={64} step={1} onValueChange={(v) => setHardware({ cpuCores: v[0] })} data-testid="hardware-cpu-select" />
        </Field>

        <Field icon={MemoryStick} label={`RAM système — ${hardware.ram} Go`}>
          <Slider value={[hardware.ram]} min={4} max={256} step={2} onValueChange={(v) => setHardware({ ram: v[0] })} data-testid="hardware-ram-input" />
        </Field>

        <Field icon={HardDrive} label="Carte graphique (GPU)">
          <Input value={hardware.gpu} onChange={(e) => setHardware({ gpu: e.target.value })} placeholder="ex: NVIDIA RTX 3060" data-testid="hardware-gpu-select" className="font-mono2 text-sm" />
        </Field>

        <Field icon={HardDrive} label={`VRAM par carte — ${hardware.vram} Go`}>
          <Slider value={[hardware.vram]} min={0} max={80} step={1} onValueChange={(v) => setHardware({ vram: v[0] })} data-testid="hardware-vram-input" />
        </Field>

        <Field icon={Layers} label={`Nombre de GPU identiques — ${gpuCount}`}>
          <Slider value={[gpuCount]} min={1} max={8} step={1} onValueChange={(v) => setHardware({ gpuCount: v[0] })} data-testid="hardware-gpu-count-input"
            disabled={hardware.accelerator === "cpu" || hardware.accelerator === "metal"} />
          <p className="font-mono2 text-[11px] text-muted-foreground" data-testid="hardware-total-vram">
            {hardware.accelerator === "cpu" || hardware.accelerator === "metal"
              ? "Non applicable avec cet accélérateur"
              : `VRAM totale : ${hardware.vram * gpuCount} Go${gpuCount > 1 ? " · Ollama / llama.cpp répartissent le modèle sur les cartes (~15 % plus lent)" : ""}`}
          </p>
        </Field>

        <Field icon={Zap} label="Accélérateur">
          <Select value={hardware.accelerator} onValueChange={(v) => setHardware({ accelerator: v })}>
            <SelectTrigger data-testid="hardware-accelerator-select" className="font-mono2 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACCELERATORS.map((a) => (
                <SelectItem key={a.id} value={a.id} className="font-mono2 text-sm">
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>
    </div>
  );
}
